/**
 * App root — composes RouterProvider + QueryClientProvider.
 *
 * QueryClient defaults for the CRO scorecard:
 *   - staleTime: 60s — cache-first reads from agg_cro_scorecard (10-min cron);
 *     60s prevents flurry of duplicate fetches on navigation.
 *   - retry: 2 — fetch-level retries on network blips; jsforce-side errors
 *     surface as 502 (SalesforceUnavailableError) which we DO NOT retry.
 *   - refetchOnWindowFocus: false — execs don't want a flicker when they
 *     tab back to the dashboard mid-conversation.
 */
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
  UnauthenticatedError,
} from './lib/api-client.js';
import { router } from './routes/router.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // Never retry 401 (we already bounced to /login), 502 (SF banner),
        // or 503 cache-not-ready (the SPA shows a polite "warming up" banner
        // and a fresh poll cycle will populate the cache).
        if (error instanceof UnauthenticatedError) return false;
        if (error instanceof SalesforceUnavailableError) return false;
        if (error instanceof CacheNotReadyError) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    // Errors are surfaced via TanStack's `isError` branch in each consumer;
    // we keep a single chokepoint here so future telemetry (Phase 6) can hook in.
  }),
});

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
